/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { sendBeacon } from '../utils/beacon';
import {
  ERROR_COMPONENT_ID_MANDATORY,
  ERROR_NOT_INITIALIZED,
} from './messages';
import { buildResponse } from './response';
import {
  getTransactionId,
  setTransactionBookingId,
  setTransactionMembershipId,
  setTransactionSearchId,
} from './transactionID';
import type {
  ClickHtmlDatameshEvent,
  ComponentDisplayHtmlDatameshEvent,
  CreateDataMeshTrackingClientArgs,
  DataMeshTrackingClient,
  MicroFrontEndConfig,
} from './types';
import { CLICK, COMPONENT_DISPLAYED, HTML_EVENTS_API_ROUTE } from './urls';

const PAGE_RENDER_ID = 'pageRenderId';

// NOTE: use global object as fallback for context and visit
const getContext = (context) => context || window?.__htmlEvents__?.eventsPath;
const getVisit = (visit) => visit || window?.__htmlEvents__?.visit;

const checkInitialized = ({ visit, context }) =>
  getVisit(visit) && getContext(context);

const baseRequest = (visit: string, mfe: MicroFrontEndConfig = {}) => {
  const href = document.location.href;
  // NOTE: rely on current implementation of getTransactionId
  const { bookingId, searchId, membershipId } = getTransactionId();
  const browserTimestamp = new Date().getTime();
  return {
    visitId: getVisit(visit),
    bookingId,
    searchId,
    membershipId,
    href,
    browserTimestamp,
    microFrontendName: mfe.name,
    microFrontendVersion: mfe.version,
  };
};

const buildClickRequest = (
  htmlElementId,
  pageRenderId,
  visit,
  mfe
): ClickHtmlDatameshEvent => ({
  ...baseRequest(visit, mfe),
  htmlElementId,
  pageRenderId,
});

const buildDisplayedRequest = (
  componentId,
  pageRenderId,
  visit,
  mfe
): ComponentDisplayHtmlDatameshEvent => ({
  ...baseRequest(visit, mfe),
  componentId,
  pageRenderId,
});

const createTrack =
  <HtmlDatameshEvent>({ path, buildRequest, getPageRenderId, mfe }) =>
  async ({
    id = undefined,
    context = undefined,
    visit = undefined,
  } = {}): Promise<{
    event: HtmlDatameshEvent;
  }> => {
    if (!checkInitialized({ visit, context })) {
      return Promise.reject(new Error(ERROR_NOT_INITIALIZED));
    }

    if (!id) {
      return Promise.reject(new Error(ERROR_COMPONENT_ID_MANDATORY));
    }

    const pageRenderId = getPageRenderId();
    const request = buildRequest(id, pageRenderId, visit, mfe);

    sendBeacon(
      `${getContext(context)}${HTML_EVENTS_API_ROUTE}${path}`,
      request
    );
    return buildResponse(request);
  };

export function createDataMeshTrackingClient({
  storageClient,
  mfe = {},
}: CreateDataMeshTrackingClientArgs): DataMeshTrackingClient {
  const getPageRenderIdBySession = () => {
    return storageClient?.get('sessionStorage')?.get(PAGE_RENDER_ID) || null;
  };

  const getPageRenderId = () => {
    return getPageRenderIdBySession() || window?.__htmlEvents__?.pageRenderId;
  };

  return {
    trackClick: createTrack<ClickHtmlDatameshEvent>({
      path: CLICK,
      buildRequest: buildClickRequest,
      getPageRenderId,
      mfe,
    }),
    trackComponentDisplayed: createTrack<ComponentDisplayHtmlDatameshEvent>({
      path: COMPONENT_DISPLAYED,
      buildRequest: buildDisplayedRequest,
      getPageRenderId,
      mfe,
    }),
    setTransactionBookingId,
    setTransactionSearchId,
    setTransactionMembershipId,
  };
}
